import type { components } from '../types/backend';

// Manual type definitions for types not exposed in OpenAPI schema
export interface Address {
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  district: string;
  land?: string | null;
  bundesland?: string | null;
}

export interface UsageSpace {
  id: string;
  info: UsageSpaceInfo;
}

export type UsageSpaceInfo = components['schemas']['UsageSpaceInfoDTO'];

export type UsageSpaceCategory = UsageSpaceInfo['category'];

export type Coordinates = components['schemas']['CoordinatesDTO'];

export type LeadCategory =
  | 'LEER'
  | 'ANTIQUITAETENHANDEL'
  | 'AUTOBAHNRASTSTAEATTE_AUTOHOF'
  | 'AUTOHAUS'
  | 'AUTOZUBEHOERFACHHANDEL'
  | 'BAECKEREI_KONDITOREI'
  | 'BAR'
  | 'BAU_GARTEN_HEIMWERKERMARKT'
  | 'BIERGARTEN'
  | 'BIERKELLER'
  | 'BILLARD_BOWLING_KEGELZENTRUM'
  | 'BISTRO'
  | 'BORDELL'
  | 'BUCHHANDEL'
  | 'CAFE'
  | 'CAMPINGPLATZ'
  | 'COPY_SHOP'
  | 'DROGERIEFACHMARKT'
  | 'EINZELHANDEL'
  | 'EISDIELE_EISCAFE'
  | 'ELEKTRO_ELEKTRONIKFACHHANDEL'
  | 'ERLEBNISGASTRONOMIE'
  | 'EROTIKSHOPS'
  | 'FAHRRADGESCHAEFT'
  | 'FANSHOP'
  | 'FLEISCHEREI_METZGEREI_WURSTWARENVERKAUF'
  | 'FLORISTIK_BLUMENHANDEL'
  | 'FOTO_FACHGESCHAEFT'
  | 'FRISOERSALON_FRISEURSALON'
  | 'GALERIE_ATELIER'
  | 'GASTHOF_GASTHAUS_MIT_UEBERNACHTUNG'
  | 'GASTSTAETTE_GASTHOF'
  | 'GETRAENKEMARKT'
  | 'HANDWERKSBETRIEB_OHNE_FRISEUR'
  | 'IMBISS_MIT_SITZPLAETZEN'
  | 'INNENAUSSTATTUNGSBEDARF'
  | 'INTERNETCAFE'
  | 'KAUFHAUS_EINKAUFSZENTRUM'
  | 'KIOSK'
  | 'KOSMETIK_TATTOO_NAGELSTUDIO'
  | 'LEBENSMITTELEINZELHANDEL'
  | 'MALEREIBEDARF_FARBENFACHHANDEL'
  | 'MOEBELHAUS'
  | 'MUSIKALIEN_INSTRUMENTEHANDEL'
  | 'MUSIKKNEIPE'
  | 'OPTIKER'
  | 'PARFUEMERIE'
  | 'PILSBAR_BIERSTUEBERL_KNEIPE'
  | 'POSTAGENTUR_POSTFILIALE'
  | 'PUB'
  | 'REISEBUERO'
  | 'RESTAURANT_SONSTIGE_INTERNATIONALE_KUECHE'
  | 'RESTAURANT_AMERIKANISCHE_KUECHE'
  | 'RESTAURANT_ARGENTINISCHES_STEAKHOUSE'
  | 'RESTAURANT_CHINESISCHE_KUECHE'
  | 'RESTAURANT_DEUTSCHE_KUECHE'
  | 'RESTAURANT_FRANZOESISCHE_KUECHE'
  | 'RESTAURANT_GRIECHISCHE_KUECHE'
  | 'RESTAURANT_INDISCHE_KUECHE'
  | 'RESTAURANT_ITALIENISCHE_KUECHE'
  | 'RESTAURANT_JAPANISCHE_KUECHE'
  | 'RESTAURANT_MEXIKANISCHE_KUECHE'
  | 'RESTAURANT_MONGOLISCHE_KUECHE'
  | 'RESTAURANT_RUSSISCHE_KUECHE'
  | 'RESTAURANT_SPANISCHE_PORTUGISISCHE_KUECHE'
  | 'RESTAURANT_THAILAEANDISCHE_KUECHE'
  | 'RESTAURANT_TUERKISCHE_KUECHE'
  | 'RESTAURANT_VIETNAMESISCHE_KUECHE'
  | 'SANITAERBEDARF'
  | 'SCHUHGESCHAEFT'
  | 'SONNENSTUDIO'
  | 'SPIELOTHEK_SPIELBANK'
  | 'SPIELWARENFACHHANDEL'
  | 'SPORT_VEREINSHEIM'
  | 'SPORTFACHHANDEL'
  | 'STEHIMBISS'
  | 'SYSTEMGASTRONOMIE'
  | 'TANKSTELLEN'
  | 'TEXTILEINZELHANDEL_OHNE_SCHUHE'
  | 'TICKET_VORVERKAUFSSTELLE'
  | 'TONTRAEGERFACHHANDEL'
  | 'UHREN_SCHMUCK_JUWELIERE'
  | 'UNBEKANNT'
  | 'VIDEOTHEK'
  | 'WEINSTUBE_WEINLOKAL'
  | 'WETTBUERO'
  | 'ZOOBEDARF'
  | 'ANDERE';

export type BusinessCategory =
  | 'LEER'
  | 'ANTIQUITAETENHANDEL'
  | 'AUTOBAHNRASTSTAEATTE_AUTOHOF'
  | 'AUTOHAUS'
  | 'AUTOZUBEHOERFACHHANDEL'
  | 'BAECKEREI_KONDITOREI'
  | 'BAR'
  | 'BAU_GARTEN_HEIMWERKERMARKT'
  | 'BIERGARTEN'
  | 'BIERKELLER'
  | 'BILLARD_BOWLING_KEGELZENTRUM'
  | 'BISTRO'
  | 'BORDELL'
  | 'BUCHHANDEL'
  | 'CABARET_EROTIK'
  | 'CAFE'
  | 'CAMPINGPLATZ'
  | 'COPY_SHOP'
  | 'DISKOTHEK'
  | 'DROGERIEFACHMARKT'
  | 'EINZELHANDEL'
  | 'EISDIELE_EISCAFE'
  | 'EISSPORTHALLE'
  | 'ELEKTRO_ELEKTRONIKFACHHANDEL'
  | 'ERLEBNISGASTRONOMIE'
  | 'EROTIKSHOPS'
  | 'FAHRRADGESCHAEFT'
  | 'FANSHOP'
  | 'FILMBAR'
  | 'FITNESSSTUDIO'
  | 'FLEISCHEREI_METZGEREI_WURSTWARENVERKAUF'
  | 'FLORISTIK_BLUMENHANDEL'
  | 'FOTO_FACHGESCHAEFT'
  | 'FRISOERSALON_FRISEURSALON'
  | 'GALERIE_ATELIER'
  | 'GASTHOF_GASTHAUS_MIT_UEBERNACHTUNG'
  | 'GASTSTAETTE_GASTHOF'
  | 'GETRAENKEMARKT'
  | 'HANDWERKSBETRIEB_OHNE_FRISEUR'
  | 'HOTEL_GARNI'
  | 'HOTEL_MOTEL'
  | 'IMBISS_MIT_SITZPLAETZEN'
  | 'INNENAUSSTATTUNGSBEDARF'
  | 'INTERNETCAFE'
  | 'KAUFHAUS_EINKAUFSZENTRUM'
  | 'KIOSK'
  | 'KOSMETIK_TATTOO_NAGELSTUDIO'
  | 'LEBENSMITTELEINZELHANDEL'
  | 'MALEREIBEDARF_FARBENFACHHANDEL'
  | 'MOEBELHAUS'
  | 'MUSIKALIEN_INSTRUMENTEHANDEL'
  | 'MUSIKKNEIPE'
  | 'OPTIKER'
  | 'PARFUEMERIE'
  | 'PARKHAEUSER_TIEFGARAGEN'
  | 'PENSION'
  | 'PILSBAR_BIERSTUEBERL_KNEIPE'
  | 'POSTAGENTUR_POSTFILIALE'
  | 'PUB'
  | 'REISEBUERO'
  | 'RESTAURANT_SONSTIGE_INTERNATIONALE_KUECHE'
  | 'RESTAURANT_AMERIKANISCHE_KUECHE'
  | 'RESTAURANT_ARGENTINISCHES_STEAKHOUSE'
  | 'RESTAURANT_CHINESISCHE_KUECHE'
  | 'RESTAURANT_DEUTSCHE_KUECHE'
  | 'RESTAURANT_FRANZOESISCHE_KUECHE'
  | 'RESTAURANT_GRIECHISCHE_KUECHE'
  | 'RESTAURANT_INDISCHE_KUECHE'
  | 'RESTAURANT_ITALIENISCHE_KUECHE'
  | 'RESTAURANT_JAPANISCHE_KUECHE'
  | 'RESTAURANT_MEXIKANISCHE_KUECHE'
  | 'RESTAURANT_MONGOLISCHE_KUECHE'
  | 'RESTAURANT_RUSSISCHE_KUECHE'
  | 'RESTAURANT_SPANISCHE_PORTUGISISCHE_KUECHE'
  | 'RESTAURANT_THAILAEANDISCHE_KUECHE'
  | 'RESTAURANT_TUERKISCHE_KUECHE'
  | 'RESTAURANT_VIETNAMESISCHE_KUECHE'
  | 'SANITAERBEDARF'
  | 'SAUNA'
  | 'SCHUHGESCHAEFT'
  | 'SCHWIMM_FREIZEIT_ERLEBNISBAD'
  | 'SONNENSTUDIO'
  | 'SPIELOTHEK_SPIELBANK'
  | 'SPIELWARENFACHHANDEL'
  | 'SPORT_VEREINSHEIM'
  | 'SPORTFACHHANDEL'
  | 'STEHIMBISS'
  | 'STRIPTEASELOKAL'
  | 'SWINGER_CLUB'
  | 'SYSTEMGASTRONOMIE'
  | 'TABLEDANCEBETRIEB'
  | 'TANKSTELLEN'
  | 'TANZBETRIEB'
  | 'TENNISHALLE'
  | 'TEXTILEINZELHANDEL_OHNE_SCHUHE'
  | 'TICKET_VORVERKAUFSSTELLE'
  | 'TONTRAEGERFACHHANDEL'
  | 'UHREN_SCHMUCK_JUWELIERE'
  | 'UNBEKANNT'
  | 'VIDEOTHEK'
  | 'WEINSTUBE_WEINLOKAL'
  | 'WETTBUERO'
  | 'ZOO_TIERGARTEN'
  | 'ZOOBEDARF';

export interface Lead {
  id: string;
  name?: string | null;
  coordinates: Coordinates;
  address: Address;
  leadCategory: LeadCategory;
  businessCategory: BusinessCategory;
  priority?: Priority | null;
  email?: string | null;
  tel?: string | null;
  website?: string | null;
  openingTimes?: components['schemas']['OpeningTimesDTO'] | null;
  usageSpaces: UsageSpace[];
  dueDate?: string | null;
  leadType?: string | null;
  operatorName?: string | null;
}

export type Log = components['schemas']['LogDTO'];

export type RoutingRequest = components['schemas']['RoutingRequestDTO'];
export type RoutingAnswer = components['schemas']['RoutingAnswer'];
export type RoutingAnswerElement =
  components['schemas']['RoutingAnswerElement'];

export type RoutingWithOpeningTimesRequest =
  components['schemas']['RoutingWithOpeningTimesRequestDTO'];

export type ExaminationResultDto =
  components['schemas']['ExaminationResultDTO'];
export type ClosedRoomExaminationResultDto =
  components['schemas']['ClosedRoomExaminationResultDTO'];
export type RoomClosedReason =
  components['schemas']['ClosedRoomExaminationResultDTO']['roomClosedReason'];
export type RoomExaminationResultDto =
  components['schemas']['RoomExaminationResultDTO'];
export type RoomExaminationResultInfoDto =
  components['schemas']['RoomExaminationResultInfoDTO'];
export type SelfCreatedRoomDto = components['schemas']['SelfCreatedRoomDTO'];
export type ImageWithDescriptionDto =
  components['schemas']['ImageWithDescriptionDTO'];
export type TariffDto = components['schemas']['TariffsDTO'];
export type TvSizeDto = components['schemas']['TvDTO']['tvSize'];
export type TvTypeDto = components['schemas']['TvDTO']['tvType'];
export type TvQuantityDto = components['schemas']['TvDTO']['tvQuantity'];

export type OpeningDay = components['schemas']['OpeningDayDTO'];
export type OpeningTimes = components['schemas']['OpeningTimesDTO'];
export type TimeRange = components['schemas']['TimeRangeDTO'];
export type TimePoint = components['schemas']['TimePointDTO'];
export type Weekday = components['schemas']['OpeningDayDTO']['weekday'];

export type ReserveRequest = components['schemas']['ReserveRequest'];
export type ReserveAnswer = components['schemas']['ReserveAnswer'];

export type Priority = 'LOW' | 'MEDIUM' | 'HIGH';

export const priorities: Priority[] = ['LOW', 'MEDIUM', 'HIGH'];

export type ModeOfTransport =
  components['schemas']['RoutingRequestDTO']['modeOfTransport'];

export const modesOfTransport: ModeOfTransport[] = ['CAR', 'BICYCLE', 'FOOT'];

export const roomClosedReasons: RoomClosedReason[] = [
  'CUSTOMER_CONTACT',
  'NOT_ACCESSIBLE',
  'NOT_EXISTENT',
];

export function translateWeekDay(weekDay: Weekday): string {
  return 'enums.weekdays.' + weekDay;
}

export const weekDays: Weekday[] = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY',
];

export const UsageSpaceCategories: UsageSpaceCategory[] = [
  'GASTRAUM',
  'VERKAUFSRAUM',
  'SAAL',
  'GESCHAEFTSRAUM',
  'FREIGELAENDE',
  'BIERGARTEN_TERRASSE_SONSTIGE_GASTFLAECHEN_IM_FREIEN',
  'FRISEURSALON',
  'OHNE_NUTZUNGSRAUM',
  'ZELT',
  'HOTEL_GAESTEZIMMER',
  'AUFENTHALTSRAUM_OHNE_BEWIRTSCHAFTUNG',
  'UNBEKANNT',
  'VERANSTALTUNGSHALLE',
  'KIRCHENRAUM',
  'VERKAUFSFLAECHE',
  'OMNIBUS_FAHRGASTRAUM',
  'FITNESSRAUM_TRAININGSFLAECHE',
  'NEBENZIMMER_GASTRONOMIE',
  'FERIENAPPARTEMENT',
  'SPORTHALLE',
  'TOILETTE',
  'BEHANDLUNGSZIMMER',
  'AUSSTELLUNGSRAUM',
  'KURSRAUM',
  'WARTEZIMMER_PRAXIS',
  'AUFENTHALTSRAUM_SPEISESAAL_SENIORENHEIM',
  'SPIELHALLE',
  'EMPFANGSRAUM_PRAXIS',
  'FOYER_LOBBY_GASTRAUM',
  'PARKPLATZ_AUSSEN',
  'BAR_GASTRONOMIE',
  'VERKAUFSFLAECHE_IM_FREIEN',
  'UNTERRICHTSRAUM',
  'FOYER_LOBBY_NICHT_BEWIRTSCHAFTET',
  'WEBSITES',
  'BUERO',
  'VEREINSZIMMER_GASTRONOMIE',
  'BEWOHNERZIMMER_SENIORENHEIM',
  'DANCEFLOOR_AREA',
  'SEMINARRAUM',
  'ZUSCHAUERRAUM_KINO',
  'JUGENDRAUM_ANERKANNTER_TRAEGER',
  'PARKFLAECHE_INNEN',
  'WERKRAUM',
  'JUGENDRAUM_JUGENDCLUB',
  'SCHALTERHALLE',
  'IMBISSBUDE_STAND_GASTRO',
  'PATIENTENZIMMER_KRANKENHAUS',
  'SCHWIMMBAD_HALLE',
  'SPEISESAAL',
  'FLUR_GANG',
  'KANTINE',
  'SAUNARAUM',
  'MESSESTAND',
  'THEATERSAAL',
  'SPORTRAUM',
  'BOWLING_KEGELBAHN',
  'FAHRGESCHAEFT',
  'SCHIFF_FAHRGASTRAUM',
  'PASSAGE',
  'UMKLEIDERAUM_KABINE',
  'MESSEHALLE',
  'VERKAUFSSTAND_AMBULANT',
  'FREIBAD',
  'IMBISSBUDE_STAND_AMBULANT',
  'BALLETTRAUM',
  'AUSSTELLUNGSFLAECHE_IM_FREIEN',
  'EISLAUFBAHN',
  'PARK',
  'SPORTPLATZ',
  'WARTEHALLE',
  'FLUGZEUGKABINE',
  'STADION',
  'KLETTERHALLE',
  'ZUSCHAUERRAUM_ZIRKUS',
  'VIDEOKABINE_EROTIK',
  'INDOORAKTIONSFLAECHE',
  'AUFZUG',
  'SPORTBAHN',
  'KAPELLE',
  'AUSSEGNUNGSHALLE',
  'WARENAUSSPIELUNG',
  'ZUG_WAGON',
  'TRAUERHALLE',
  'VIDEOVORFUEHRPLATZ',
  'TRAMPOLINHALLE',
  'PRIVATRAUM',
  'HAFTRAUM',
  'ANKUNFTS_UND_ABFLUGBEREICHE',
  'KUPPELSAAL',
  'TRAUZIMMER',
  'VIDEOKABINE_NORMAL',
];

export const USAGE_SPACE_CATEGORY_EMOJIS: Record<UsageSpaceCategory, string> = {
  GASTRAUM: '🍴',
  VERKAUFSRAUM: '🛒',
  SAAL: '🏛️',
  GESCHAEFTSRAUM: '🏢',
  FREIGELAENDE: '🌳',
  BIERGARTEN_TERRASSE_SONSTIGE_GASTFLAECHEN_IM_FREIEN: '🍺',
  FRISEURSALON: '✂️',
  OHNE_NUTZUNGSRAUM: '❓',
  ZELT: '⛺',
  HOTEL_GAESTEZIMMER: '🛏️',
  AUFENTHALTSRAUM_OHNE_BEWIRTSCHAFTUNG: '🛋️',
  UNBEKANNT: '', // unknown room category — placeholder value, no emoji
  VERANSTALTUNGSHALLE: '🎫',
  KIRCHENRAUM: '⛪',
  VERKAUFSFLAECHE: '🛒',
  OMNIBUS_FAHRGASTRAUM: '🚌',
  FITNESSRAUM_TRAININGSFLAECHE: '💪',
  NEBENZIMMER_GASTRONOMIE: '🍴',
  FERIENAPPARTEMENT: '🏖️',
  SPORTHALLE: '🏀',
  TOILETTE: '🚻',
  BEHANDLUNGSZIMMER: '💉',
  AUSSTELLUNGSRAUM: '🖼️',
  KURSRAUM: '📖',
  WARTEZIMMER_PRAXIS: '💉',
  AUFENTHALTSRAUM_SPEISESAAL_SENIORENHEIM: '👵',
  SPIELHALLE: '🎰',
  EMPFANGSRAUM_PRAXIS: '💉',
  FOYER_LOBBY_GASTRAUM: '🛒',
  PARKPLATZ_AUSSEN: '🅿️',
  BAR_GASTRONOMIE: '🍺',
  VERKAUFSFLAECHE_IM_FREIEN: '🌳',
  UNTERRICHTSRAUM: '📖',
  FOYER_LOBBY_NICHT_BEWIRTSCHAFTET: '🚪',
  WEBSITES: '🌐',
  BUERO: '🏢',
  VEREINSZIMMER_GASTRONOMIE: '🍴',
  BEWOHNERZIMMER_SENIORENHEIM: '👴',
  DANCEFLOOR_AREA: '🪩',
  SEMINARRAUM: '📖',
  ZUSCHAUERRAUM_KINO: '🎬',
  JUGENDRAUM_ANERKANNTER_TRAEGER: '🧑',
  PARKFLAECHE_INNEN: '🅿️',
  WERKRAUM: '🛠️',
  JUGENDRAUM_JUGENDCLUB: '🧒',
  SCHALTERHALLE: '🏦',
  IMBISSBUDE_STAND_GASTRO: '🌭',
  PATIENTENZIMMER_KRANKENHAUS: '💉',
  SCHWIMMBAD_HALLE: '🏊',
  SPEISESAAL: '🍴',
  FLUR_GANG: '🚶',
  KANTINE: '🍴',
  SAUNARAUM: '🧖',
  MESSESTAND: '🎫',
  THEATERSAAL: '🎭',
  SPORTRAUM: '🏋️',
  BOWLING_KEGELBAHN: '🎳',
  FAHRGESCHAEFT: '🎡',
  SCHIFF_FAHRGASTRAUM: '🚢',
  PASSAGE: '🚶',
  UMKLEIDERAUM_KABINE: '👔',
  MESSEHALLE: '🏢',
  VERKAUFSSTAND_AMBULANT: '🛒',
  FREIBAD: '🏊',
  IMBISSBUDE_STAND_AMBULANT: '🌭',
  BALLETTRAUM: '🩰',
  AUSSTELLUNGSFLAECHE_IM_FREIEN: '🖼️',
  EISLAUFBAHN: '⛸️',
  PARK: '🌳',
  SPORTPLATZ: '⚽',
  WARTEHALLE: '🪑',
  FLUGZEUGKABINE: '✈️',
  STADION: '⚽',
  KLETTERHALLE: '🧗',
  ZUSCHAUERRAUM_ZIRKUS: '🎪',
  VIDEOKABINE_EROTIK: '🔞',
  INDOORAKTIONSFLAECHE: '🎮',
  AUFZUG: '🛗',
  SPORTBAHN: '🏃',
  KAPELLE: '⛪',
  AUSSEGNUNGSHALLE: '🕊️',
  WARENAUSSPIELUNG: '🎰',
  ZUG_WAGON: '🚃',
  TRAUERHALLE: '🕯️',
  VIDEOVORFUEHRPLATZ: '🎬',
  TRAMPOLINHALLE: '🤸',
  PRIVATRAUM: '🔒',
  HAFTRAUM: '🚨',
  ANKUNFTS_UND_ABFLUGBEREICHE: '🛫',
  KUPPELSAAL: '🏛️',
  TRAUZIMMER: '💍',
  VIDEOKABINE_NORMAL: '🎬',
};

export function getEmojiForCategory(category: UsageSpaceCategory): string {
  return USAGE_SPACE_CATEGORY_EMOJIS[category] ?? '';
}

export function translateUsageSpaceCategory(
  category: UsageSpaceCategory,
): string {
  return 'enums.category.' + category;
}

export function getUsageSpaceCategoryWithEmoji(
  category: UsageSpaceCategory,
  translateService: { instant: (key: string) => string },
): string {
  const emoji = getEmojiForCategory(category);
  const translation = translateService.instant(translateUsageSpaceCategory(category));
  return emoji ? `${emoji} ${translation}` : translation;
}

export function getSortedUsageSpaceCategories(
  translateService: { instant: (key: string) => string },
): UsageSpaceCategory[] {
  return [...UsageSpaceCategories].sort((a, b) => {
    const translatedA = translateService.instant(translateUsageSpaceCategory(a));
    const translatedB = translateService.instant(translateUsageSpaceCategory(b));
    return translatedA.localeCompare(translatedB, 'de', {sensitivity: 'base'});
  });
}

export const LeadCategories: LeadCategory[] = [
  'LEER',
  'ANTIQUITAETENHANDEL',
  'AUTOBAHNRASTSTAEATTE_AUTOHOF',
  'AUTOHAUS',
  'AUTOZUBEHOERFACHHANDEL',
  'BAECKEREI_KONDITOREI',
  'BAR',
  'BAU_GARTEN_HEIMWERKERMARKT',
  'BIERGARTEN',
  'BIERKELLER',
  'BILLARD_BOWLING_KEGELZENTRUM',
  'BISTRO',
  'BORDELL',
  'BUCHHANDEL',
  'CAFE',
  'CAMPINGPLATZ',
  'COPY_SHOP',
  'DROGERIEFACHMARKT',
  'EINZELHANDEL',
  'EISDIELE_EISCAFE',
  'ELEKTRO_ELEKTRONIKFACHHANDEL',
  'ERLEBNISGASTRONOMIE',
  'EROTIKSHOPS',
  'FAHRRADGESCHAEFT',
  'FANSHOP',
  'FLEISCHEREI_METZGEREI_WURSTWARENVERKAUF',
  'FLORISTIK_BLUMENHANDEL',
  'FOTO_FACHGESCHAEFT',
  'FRISOERSALON_FRISEURSALON',
  'GALERIE_ATELIER',
  'GASTHOF_GASTHAUS_MIT_UEBERNACHTUNG',
  'GASTSTAETTE_GASTHOF',
  'GETRAENKEMARKT',
  'HANDWERKSBETRIEB_OHNE_FRISEUR',
  'IMBISS_MIT_SITZPLAETZEN',
  'INNENAUSSTATTUNGSBEDARF',
  'INTERNETCAFE',
  'KAUFHAUS_EINKAUFSZENTRUM',
  'KIOSK',
  'KOSMETIK_TATTOO_NAGELSTUDIO',
  'LEBENSMITTELEINZELHANDEL',
  'MALEREIBEDARF_FARBENFACHHANDEL',
  'MOEBELHAUS',
  'MUSIKALIEN_INSTRUMENTEHANDEL',
  'MUSIKKNEIPE',
  'OPTIKER',
  'PARFUEMERIE',
  'PILSBAR_BIERSTUEBERL_KNEIPE',
  'POSTAGENTUR_POSTFILIALE',
  'PUB',
  'REISEBUERO',
  'RESTAURANT_SONSTIGE_INTERNATIONALE_KUECHE',
  'RESTAURANT_AMERIKANISCHE_KUECHE',
  'RESTAURANT_ARGENTINISCHES_STEAKHOUSE',
  'RESTAURANT_CHINESISCHE_KUECHE',
  'RESTAURANT_DEUTSCHE_KUECHE',
  'RESTAURANT_FRANZOESISCHE_KUECHE',
  'RESTAURANT_GRIECHISCHE_KUECHE',
  'RESTAURANT_INDISCHE_KUECHE',
  'RESTAURANT_ITALIENISCHE_KUECHE',
  'RESTAURANT_JAPANISCHE_KUECHE',
  'RESTAURANT_MEXIKANISCHE_KUECHE',
  'RESTAURANT_MONGOLISCHE_KUECHE',
  'RESTAURANT_RUSSISCHE_KUECHE',
  'RESTAURANT_SPANISCHE_PORTUGISISCHE_KUECHE',
  'RESTAURANT_THAILAEANDISCHE_KUECHE',
  'RESTAURANT_TUERKISCHE_KUECHE',
  'RESTAURANT_VIETNAMESISCHE_KUECHE',
  'SANITAERBEDARF',
  'SCHUHGESCHAEFT',
  'SONNENSTUDIO',
  'SPIELOTHEK_SPIELBANK',
  'SPIELWARENFACHHANDEL',
  'SPORT_VEREINSHEIM',
  'SPORTFACHHANDEL',
  'STEHIMBISS',
  'SYSTEMGASTRONOMIE',
  'TANKSTELLEN',
  'TEXTILEINZELHANDEL_OHNE_SCHUHE',
  'TICKET_VORVERKAUFSSTELLE',
  'TONTRAEGERFACHHANDEL',
  'UHREN_SCHMUCK_JUWELIERE',
  'UNBEKANNT',
  'VIDEOTHEK',
  'WEINSTUBE_WEINLOKAL',
  'WETTBUERO',
  'ZOOBEDARF',
  'ANDERE',
];

export function translateCategory(leadCategory: LeadCategory): string {
  return 'enums.leadCategory.' + leadCategory;
}

export const LEAD_CATEGORY_EMOJIS: Record<LeadCategory, string> = {
  LEER: '', // empty/undetermined location category — placeholder value, no emoji
  ANTIQUITAETENHANDEL: '🏺',
  AUTOBAHNRASTSTAEATTE_AUTOHOF: '⛽',
  AUTOHAUS: '🚗',
  AUTOZUBEHOERFACHHANDEL: '🔧',
  BAECKEREI_KONDITOREI: '🥐',
  BAR: '🍸',
  BAU_GARTEN_HEIMWERKERMARKT: '🛠️',
  BIERGARTEN: '🍺',
  BIERKELLER: '🍺',
  BILLARD_BOWLING_KEGELZENTRUM: '🎳',
  BISTRO: '🍴',
  BORDELL: '🔞',
  BUCHHANDEL: '📖',
  CAFE: '☕',
  CAMPINGPLATZ: '⛺',
  COPY_SHOP: '🖨️',
  DROGERIEFACHMARKT: '💊',
  EINZELHANDEL: '🛒',
  EISDIELE_EISCAFE: '🍦',
  ELEKTRO_ELEKTRONIKFACHHANDEL: '💡',
  ERLEBNISGASTRONOMIE: '🎭',
  EROTIKSHOPS: '🔞',
  FAHRRADGESCHAEFT: '🚲',
  FANSHOP: '⚽',
  FLEISCHEREI_METZGEREI_WURSTWARENVERKAUF: '🥩',
  FLORISTIK_BLUMENHANDEL: '💐',
  FOTO_FACHGESCHAEFT: '📷',
  FRISOERSALON_FRISEURSALON: '✂️',
  GALERIE_ATELIER: '🖼️',
  GASTHOF_GASTHAUS_MIT_UEBERNACHTUNG: '🛏️',
  GASTSTAETTE_GASTHOF: '🍴',
  GETRAENKEMARKT: '🥤',
  HANDWERKSBETRIEB_OHNE_FRISEUR: '🔨',
  IMBISS_MIT_SITZPLAETZEN: '🌭',
  INNENAUSSTATTUNGSBEDARF: '🪑',
  INTERNETCAFE: '🌐',
  KAUFHAUS_EINKAUFSZENTRUM: '🛒',
  KIOSK: '🏪',
  KOSMETIK_TATTOO_NAGELSTUDIO: '💅',
  LEBENSMITTELEINZELHANDEL: '🛒',
  MALEREIBEDARF_FARBENFACHHANDEL: '🎨',
  MOEBELHAUS: '🪑',
  MUSIKALIEN_INSTRUMENTEHANDEL: '🎸',
  MUSIKKNEIPE: '🎵',
  OPTIKER: '👓',
  PARFUEMERIE: '🌸',
  PILSBAR_BIERSTUEBERL_KNEIPE: '🍺',
  POSTAGENTUR_POSTFILIALE: '✉️',
  PUB: '🍺',
  REISEBUERO: '✈️',
  RESTAURANT_SONSTIGE_INTERNATIONALE_KUECHE: '🍴',
  RESTAURANT_AMERIKANISCHE_KUECHE: '🍔',
  RESTAURANT_ARGENTINISCHES_STEAKHOUSE: '🥩',
  RESTAURANT_CHINESISCHE_KUECHE: '🍜',
  RESTAURANT_DEUTSCHE_KUECHE: '🥨',
  RESTAURANT_FRANZOESISCHE_KUECHE: '🥐',
  RESTAURANT_GRIECHISCHE_KUECHE: '🫒',
  RESTAURANT_INDISCHE_KUECHE: '🍛',
  RESTAURANT_ITALIENISCHE_KUECHE: '🍕',
  RESTAURANT_JAPANISCHE_KUECHE: '🍣',
  RESTAURANT_MEXIKANISCHE_KUECHE: '🌮',
  RESTAURANT_MONGOLISCHE_KUECHE: '🍖',
  RESTAURANT_RUSSISCHE_KUECHE: '🥟',
  RESTAURANT_SPANISCHE_PORTUGISISCHE_KUECHE: '🥘',
  RESTAURANT_THAILAEANDISCHE_KUECHE: '🍜',
  RESTAURANT_TUERKISCHE_KUECHE: '🥙',
  RESTAURANT_VIETNAMESISCHE_KUECHE: '🍜',
  SANITAERBEDARF: '🚿',
  SCHUHGESCHAEFT: '👟',
  SONNENSTUDIO: '☀️',
  SPIELOTHEK_SPIELBANK: '🎰',
  SPIELWARENFACHHANDEL: '🧸',
  SPORT_VEREINSHEIM: '⚽',
  SPORTFACHHANDEL: '🏋️',
  STEHIMBISS: '🌭',
  SYSTEMGASTRONOMIE: '🍔',
  TANKSTELLEN: '⛽',
  TEXTILEINZELHANDEL_OHNE_SCHUHE: '👗',
  TICKET_VORVERKAUFSSTELLE: '🎫',
  TONTRAEGERFACHHANDEL: '💿',
  UHREN_SCHMUCK_JUWELIERE: '💍',
  UNBEKANNT: '❓',
  VIDEOTHEK: '🎬',
  WEINSTUBE_WEINLOKAL: '🍷',
  WETTBUERO: '🎲',
  ZOOBEDARF: '🐾',
  ANDERE: '🏷️',
};

export function getEmojiForLeadCategory(category: LeadCategory): string {
  return LEAD_CATEGORY_EMOJIS[category] ?? '';
}

export function getLeadCategoryWithEmoji(
  category: LeadCategory,
  translateService: { instant: (key: string) => string },
): string {
  const emoji = getEmojiForLeadCategory(category);
  const translation = translateService.instant(translateCategory(category));
  return emoji ? `${emoji} ${translation}` : translation;
}

export function getSortedLeadCategories(
  translateService: { instant: (key: string) => string },
): LeadCategory[] {
  // LEER, UNBEKANNT and ANDERE are placeholders/catch-alls and must not be selectable.
  const excluded: LeadCategory[] = ['LEER', 'UNBEKANNT', 'ANDERE'];
  return LeadCategories.filter((category) => !excluded.includes(category)).sort((a, b) => {
    const translatedA = translateService.instant(translateCategory(a));
    const translatedB = translateService.instant(translateCategory(b));
    return translatedA.localeCompare(translatedB, 'de', {sensitivity: 'base'});
  });
}
